// bookingHelpers.ts
// Pure helpers for the trial booking form. No React, no styled-components.

const DAY_NAMES = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
];

/**
 * Given YYYY-MM-DD date strings, return the distinct weekday names present.
 * UTC-safe so the weekday does not shift with the server timezone.
 */
export function deriveAvailableDays(dates: string[]): string[] {
    const set = new Set<string>();
    for (const d of dates) {
        const [year, month, day] = d.split("-").map(Number);
        if (!year || !month || !day) continue;
        const dt = new Date(Date.UTC(year, month - 1, day));
        set.add(DAY_NAMES[dt.getUTCDay()]);
    }
    return Array.from(set);
}

/**
 * Build the /api/slots query URL. province is included only for id.
 */
export function buildSlotsUrl(opts: {
    country: string;
    province: string;
    pool: string;
}): string {
    const params = new URLSearchParams();
    params.set("country", opts.country);
    params.set("pool", opts.pool);
    if (opts.country === "id" && opts.province) {
        params.set("province", opts.province);
    }
    return `/api/slots?${params.toString()}`;
}

/**
 * Build the /api/pools query URL. province is included only for id.
 */
export function buildPoolsUrl(opts: {
    country: string;
    province: string;
}): string {
    const params = new URLSearchParams();
    params.set("country", opts.country);
    if (opts.country === "id" && opts.province) {
        params.set("province", opts.province);
    }
    return `/api/pools?${params.toString()}`;
}

export interface PoolChoice {
    slug: string;
    label: string;
    region: string | null;
}

/**
 * The pool list to show if /api/pools cannot be reached.
 *
 * A booking form with an empty location dropdown converts nobody, so a network
 * blip must not take the form down. This mirrors what the dropdown hardcoded
 * before it became data-driven; it can go stale, which is exactly why it is
 * only ever a fallback. Hougang is included — it has live Airtable slots and
 * was missing from the old hardcoded list, which is what made it unbookable.
 */
export const FALLBACK_POOLS: Record<string, PoolChoice[]> = {
    sg: [
        { slug: "bishan", label: "Bishan Swimming Complex", region: "Central" },
        {
            slug: "pasir-ris",
            label: "Pasir Ris Swimming Complex",
            region: "East"
        },
        {
            slug: "hougang",
            label: "Hougang Swimming Complex",
            region: "North-East"
        },
        {
            slug: "canberra",
            label: "Canberra Swimming Complex",
            region: "North"
        },
        {
            slug: "clementi",
            label: "Clementi Swimming Complex",
            region: "West"
        },
        {
            slug: "jurong-west",
            label: "Jurong West Swimming Complex",
            region: "West"
        }
    ],
    jakarta: [
        { slug: "abinaya", label: "Abinaya", region: null },
        { slug: "bojana", label: "Bojana", region: null },
        { slug: "citra-1", label: "Citra 1", region: null },
        { slug: "city-club", label: "City Club", region: null },
        { slug: "cobra", label: "Cobra", region: null },
        { slug: "faletehan", label: "Faletehan", region: null },
        { slug: "halim", label: "Halim", region: null },
        { slug: "harapan-baru", label: "Harapan Baru", region: null },
        { slug: "jaya-yudha", label: "Jaya Yudha", region: null },
        { slug: "merlynn", label: "Merlynn", region: null },
        { slug: "perahu", label: "Perahu", region: null },
        { slug: "persada-bogor", label: "Persada Bogor", region: null },
        { slug: "persada-raya", label: "Persada Raya", region: null },
        { slug: "plaza-festival", label: "Plaza Festival", region: null },
        { slug: "sepolwan", label: "Sepolwan", region: null },
        { slug: "tamansari", label: "Tamansari", region: null },
        { slug: "tirta-yudha", label: "Tirta Yudha", region: null },
        { slug: "tirto-sagoro-08", label: "Tirto Sagoro 08", region: null },
        { slug: "usman", label: "Usman", region: null }
    ],
    bali: [
        { slug: "augi-sport-centre", label: "Augi Sport Centre", region: null },
        { slug: "graha-socio", label: "Graha Socio", region: null }
    ],
    medan: [{ slug: "tasbi-setiabudi", label: "Tasbi/Setiabudi", region: null }]
};

/** Fallback pools for a country/province combination. */
export function fallbackPoolsFor(
    country: string,
    province: string
): PoolChoice[] {
    if (country === "sg") return FALLBACK_POOLS.sg;
    return FALLBACK_POOLS[province] ?? [];
}

/**
 * Group pools into optgroups, preserving the order they arrive in.
 * Pools with region null land in a single unnamed group (the ID flat list).
 */
export function groupPoolsByRegion(
    pools: PoolChoice[]
): { region: string | null; pools: PoolChoice[] }[] {
    const groups: { region: string | null; pools: PoolChoice[] }[] = [];
    for (const pool of pools) {
        const last = groups[groups.length - 1];
        if (last && last.region === pool.region) last.pools.push(pool);
        else groups.push({ region: pool.region, pools: [pool] });
    }
    return groups;
}
